import React, { useState } from 'react';
import { MdWarningAmber, MdCheckCircle, MdExpandMore } from 'react-icons/md';

// What changed between this version and the one it supersedes.
//
// TWO READINGS, SHOWN SEPARATELY AND ON PURPOSE. "Tracked" is what Word recorded. "The
// comparison" is what diffing the two texts found, and it runs whether or not Word recorded
// anything — because someone can edit with tracking off and Word will report nothing at all.
//
// The `untracked` verdict is therefore the loud one: the text changed and Word did not say
// so. Either tracking was off by accident or the change was not meant to be noticed, and a
// reviewer needs to know either way.
//
// Note what is NOT here: a green "no changes" that could ever be shown because the markup
// was merely absent. Only an empty comparison earns that, which the server enforces.

const TONE = {
  unchanged: 'border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-300',
  untracked: 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200',
  tracked: 'border-blue-500 bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:text-blue-200',
};

export default function ChangeSummary({ summary, compact = false }) {
  const [open, setOpen] = useState(!compact);
  if (!summary?.verdict) return null;

  const { verdict, change, tracked, compared_with: comparedWith } = summary;
  const counts = change?.counts || {};
  const authors = tracked?.stats?.authors || [];

  return (
    <section className="mt-3">
      <div className={`rounded-md border-l-2 px-3 py-2.5 text-[12px] leading-relaxed ${TONE[verdict.state] || TONE.tracked}`}>
        <span className="flex items-start gap-2">
          {verdict.state === 'unchanged'
            ? <MdCheckCircle size={15} className="mt-0.5 shrink-0" />
            : <MdWarningAmber size={15} className="mt-0.5 shrink-0" />}
          <span>
            {verdict.message}
            {comparedWith && (
              <span className="opacity-70"> Compared with version {comparedWith}.</span>
            )}
          </span>
        </span>
      </div>

      {(counts.changed || counts.added || counts.removed) ? (
        <>
          <button type="button" onClick={() => setOpen((v) => !v)}
                  className="mt-2 flex items-center gap-1 text-[11.5px] font-medium text-gray-600 hover:text-brand-gold dark:text-gray-300">
            <MdExpandMore size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            {counts.changed || 0} changed · {counts.added || 0} added · {counts.removed || 0} removed
          </button>

          {open && (
            <div className="mt-2 space-y-2">
              {(change.changed || []).map((c) => (
                <div key={`c-${c.number}-${c.heading}`} className="rounded border border-gray-200 p-2.5 dark:border-gray-800">
                  <div className="text-[12px] font-medium text-gray-900 dark:text-gray-100">
                    {c.number && <span className="mr-1.5 tabular-nums">{c.number}</span>}
                    {c.heading}
                  </div>
                  {c.heading_before && (
                    <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      Heading was: {c.heading_before}
                    </div>
                  )}
                  <div className="mt-1.5 space-y-1">
                    {(c.ops || []).map((op, i) => (
                      <p key={i} className="text-[12px] leading-relaxed">
                        {op.before && (
                          <span className="rounded bg-rose-500/10 px-1 text-rose-800 line-through dark:text-rose-300">
                            {op.before}
                          </span>
                        )}
                        {op.before && op.after ? ' ' : null}
                        {op.after && (
                          <span className="rounded bg-emerald-500/10 px-1 text-emerald-800 dark:text-emerald-300">
                            {op.after}
                          </span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              {(change.added || []).map((a) => (
                <Block key={`a-${a.number}-${a.heading}`} tone="emerald" label="Added"
                       number={a.number} heading={a.heading} text={a.text} />
              ))}
              {/* Deleted text is shown in full: "a clause was removed" is not reviewable. */}
              {(change.removed || []).map((r) => (
                <Block key={`r-${r.number}-${r.heading}`} tone="rose" label="Removed"
                       number={r.number} heading={r.heading} text={r.text} />
              ))}
            </div>
          )}
        </>
      ) : null}

      {authors.length > 0 && (
        <p className="mt-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          Word attributes its tracked changes to{' '}
          {authors.map((a) => `${a.author} (+${a.inserted}/−${a.deleted} characters)`).join(', ')}.{' '}
          {/* The name in a Word file is set by whoever edited it and can be anything. */}
          <span className="italic">
            That is the name recorded in the file, which the editor sets — not proof of who
            made the change.
          </span>
        </p>
      )}
    </section>
  );
}

function Block({ tone, label, number, heading, text }) {
  const cls = tone === 'emerald'
    ? 'border-emerald-500/40 bg-emerald-500/[0.06]'
    : 'border-rose-500/40 bg-rose-500/[0.06]';
  return (
    <div className={`rounded border-l-2 p-2.5 ${cls}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-0.5 text-[12px] font-medium text-gray-900 dark:text-gray-100">
        {number && <span className="mr-1.5 tabular-nums">{number}</span>}{heading}
      </div>
      {text && (
        <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">
          {text}
        </p>
      )}
    </div>
  );
}
